"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  FileText,
  Globe,
  Inbox,
  Plus,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  type LucideIcon
} from "lucide-react";
import { GROUPS } from "../components/console-shell";
import { Banner, Button, Desk } from "../components/desk";
import { Skeleton } from "../components/ui/skeleton";
import { api, formatTime } from "../lib/api";
import { readAttention, sweepReminders } from "../lib/workbench-api";
import { WorkbenchPanels, WorkbenchDataStatus, type WorkbenchState } from "../components/desk/workbench-panels";
import { useSession } from "../lib/session";

type Kind = "activity" | "notice" | "content";
type WorkRow = {
  id: string;
  title: string;
  updatedAt: string;
  organization: { name: string };
  kind: Kind;
};
type Upcoming = {
  id: string;
  title: string;
  startAt: string;
  endAt: string;
  location: string;
  status: string;
};
type SourceState = { rows: WorkRow[]; loading: boolean; failed: boolean };
const KINDS: Record<Kind, { label: string; icon: LucideIcon; path: string }> = {
  activity: { label: "活动", icon: CalendarDays, path: "/activities" },
  notice: { label: "通知", icon: FileText, path: "/notices" },
  content: { label: "门户内容", icon: Globe, path: "/portal" }
};

export default function Home() {
  const { whoami, hasRole } = useSession();
  const coordinator = hasRole("ACTIVITY_COORDINATOR");
  const author = hasRole("CONTENT_AUTHOR");
  const [sources, setSources] = useState<Partial<Record<Kind, SourceState>>>({});
  const [upcoming, setUpcoming] = useState<{
    items: Upcoming[];
    loading: boolean;
    failed: boolean;
  }>({ items: [], loading: true, failed: false });
  const [filter, setFilter] = useState<Kind | "all">("all");
  const [refresh, setRefresh] = useState(0);
  const [workbench, setWorkbench] = useState<WorkbenchState>({ data: null, loading: true, failed: false });
  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState("");
  const canReadAttention = (whoami?.roles ?? []).some(role => !["MEMBER_SELF", "BREAK_GLASS"].includes(role.roleCode) && role.reviewStatus !== "PENDING_REVIEW");
  useEffect(() => {
    let current = true;
    setWorkbench({ data: null, loading: canReadAttention, failed: false });
    if (canReadAttention) void readAttention().then(data => { if (current) setWorkbench({ data, loading: false, failed: false }); }).catch(() => { if (current) setWorkbench({ data: null, loading: false, failed: true }); });
    return () => { current = false; };
  }, [whoami?.accountId, canReadAttention, refresh]);
  async function scan() {
    setScanning(true); setScanMessage("");
    try { const result = await sweepReminders(`wo96_sweep_${crypto.randomUUID()}`); setScanMessage(result.status === "COMPLETED" ? `扫描完成，新增 ${result.dispatchedCount} 条提醒留痕。` : "扫描未完成，请核对后重试。"); setRefresh(v => v + 1); }
    catch { setScanMessage("扫描暂未完成，请核对授权后重试。"); }
    finally { setScanning(false); }
  }
  const [readAt, setReadAt] = useState("");
  // 季度提醒在读取时推出（ADR-0032 §3.6）：上一季度还没有公益统计任务，才挂一条提醒。
  const socialService = hasRole("SOCIAL_SERVICE_COORDINATOR");
  const [charityDue, setCharityDue] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    setCharityDue(null);
    if (socialService)
      void api<{ due?: { label: string } }>("/api/v1/charity/reminder")
        .then((result) => { if (current) setCharityDue(result.due?.label ?? null); })
        .catch(() => undefined);
    return () => { current = false; };
  }, [socialService, whoami?.accountId, refresh]);
  const awaitingReview = (whoami?.roles ?? []).filter(
    (role) => role.reviewStatus === "PENDING_REVIEW"
  );

  useEffect(() => {
    let current = true;
    setSources({});
    setReadAt("");
    const requests: Array<{ kind: Kind; path: string }> = [];
    if (coordinator) requests.push({ kind: "activity", path: "/api/v1/activities?status=DRAFT" });
    if (author)
      requests.push(
        { kind: "notice", path: "/api/v1/notification-drafts?status=DRAFT" },
        { kind: "content", path: "/api/v1/portal-contents?status=DRAFT" }
      );
    for (const { kind, path } of requests) {
      setSources((previous) => ({
        ...previous,
        [kind]: { rows: [], loading: true, failed: false }
      }));
      void api<{ items: Omit<WorkRow, "kind">[] }>(path)
        .then((result) => {
          if (!current) return;
          setSources((previous) => ({
            ...previous,
            [kind]: {
              rows: result.items.map((row) => ({ ...row, kind })),
              loading: false,
              failed: false
            }
          }));
          setReadAt(formatTime(new Date().toISOString()));
        })
        .catch(() => {
          if (current)
            setSources((previous) => ({
              ...previous,
              [kind]: { rows: [], loading: false, failed: true }
            }));
        });
    }
    if (coordinator) {
      setUpcoming({ items: [], loading: true, failed: false });
      void api<{ items: Upcoming[] }>("/api/v1/activities?status=PUBLISHED")
        .then((result) => {
          if (!current) return;
          const now = Date.now();
          setUpcoming({
            items: result.items
              .filter((row) => new Date(row.endAt).getTime() >= now)
              .sort((a, b) => a.startAt.localeCompare(b.startAt))
              .slice(0, 3),
            loading: false,
            failed: false
          });
        })
        .catch(() => {
          if (current) setUpcoming({ items: [], loading: false, failed: true });
        });
    } else setUpcoming({ items: [], loading: false, failed: false });
    return () => {
      current = false;
    };
  }, [coordinator, author, whoami?.accountId, refresh]);

  const enabledKinds = (Object.keys(KINDS) as Kind[]).filter((kind) =>
    kind === "activity" ? coordinator : author
  );
  const loading = enabledKinds.some((kind) => !sources[kind] || sources[kind]?.loading);
  const failedKinds = enabledKinds.filter((kind) => sources[kind]?.failed);
  const rows = Object.values(sources)
    .flatMap((source) => source.rows)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const visibleRows = rows.filter((row) => filter === "all" || row.kind === filter).slice(0, 8);
  const shortcuts = GROUPS.flatMap((group) => group.items)
    .filter((item) => item.role && (item.roles ?? [item.role]).some(hasRole))
    .slice(0, 5);

  return (
    <Desk wide>
      <div className="workspace">
        <header className="workspace-heading">
          <div>
            <div className="workspace-eyebrow">会务有序 · 履职有据</div>
            <h1 className="workspace-title">手头工作</h1>
            <p className="workspace-subtitle">整理一份材料，准备一场活动，接续每一项工作。</p>
          </div>
          <div className="workspace-actions">
            <Button
              variant="ghost"
              size="icon"
              aria-label="刷新工作清单"
              onClick={() => setRefresh((value) => value + 1)}
              disabled={loading}
            >
              <RefreshCw className="size-4" />
            </Button>
            {coordinator ? (
              <Button asChild>
                <Link href="/activities?mode=create">
                  <Plus className="size-4" />
                  新建活动
                </Link>
              </Button>
            ) : author ? (
              <Button asChild>
                <Link href="/notices">
                  进入通知起草
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            ) : null}
          </div>
        </header>
        {charityDue ? (
          <Banner tone="warning">
            {charityDue}。发布后全体有效会员收到站内通知。
            <Link href="/charity" className="ml-2 underline">
              去发布
            </Link>
          </Banner>
        ) : null}
        {awaitingReview.length > 0 ? (
          <Banner tone="warning">
            你有 {awaitingReview.length} 项授权待复核，生效后将显示相应工作入口。
            <Link href="/authority" className="ml-2 underline">
              查看授权
            </Link>
          </Banner>
        ) : null}
        <WorkbenchPanels state={workbench} retry={() => setRefresh(v => v + 1)} scan={hasRole("ORGANIZATION_ADMIN") ? () => void scan() : undefined} scanning={scanning} scanMessage={scanMessage} />
        <div className="home-layout">
          <div className="home-main">
            <section aria-label="最近处理">
              <div className="workspace-section-head">
                <div>
                  <h2>最近处理</h2>
                  <p>草稿与准备事项 · 按最近更新排列</p>
                </div>
                <Inbox className="size-4 text-muted-foreground" aria-hidden />
              </div>
              {enabledKinds.length > 0 ? (
                <div className="workspace-tabs" aria-label="草稿类别">
                  <button
                    type="button"
                    aria-pressed={filter === "all"}
                    onClick={() => setFilter("all")}
                  >
                    全部
                  </button>
                  {enabledKinds.map((kind) => (
                    <button
                      type="button"
                      key={kind}
                      aria-pressed={filter === kind}
                      onClick={() => setFilter(kind)}
                    >
                      {KINDS[kind].label}
                    </button>
                  ))}
                </div>
              ) : null}
              {failedKinds.length > 0 ? (
                <div className="workspace-error" role="alert">
                  <span>
                    {failedKinds.map((kind) => KINDS[kind].label).join("、")}草稿暂时未能读取。
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRefresh((value) => value + 1)}
                  >
                    重新读取
                  </Button>
                </div>
              ) : null}
              {loading ? (
                <div className="workspace-loading" role="status" aria-label="正在读取草稿">
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </div>
              ) : visibleRows.length > 0 ? (
                <ul className="home-worklist">
                  {visibleRows.map((row) => {
                    const kind = KINDS[row.kind];
                    const Icon = kind.icon;
                    const href =
                      row.kind === "activity"
                        ? `/activities?status=DRAFT&activity=${encodeURIComponent(row.id)}`
                        : `${kind.path}?record=${encodeURIComponent(row.id)}`;
                    return (
                      <li key={`${row.kind}-${row.id}`}>
                        <Link href={href} className="home-workitem">
                          <span className="home-workitem__icon">
                            <Icon className="size-5" strokeWidth={1.5} aria-hidden />
                          </span>
                          <span>
                            <span className="home-workitem__title">{row.title}</span>
                            <span className="home-workitem__meta">
                              <span>{kind.label}草稿</span>
                              <span>{row.organization.name}</span>
                              <time dateTime={row.updatedAt}>{formatTime(row.updatedAt)}</time>
                            </span>
                          </span>
                          <span className="home-workitem__action">
                            <span>查看草稿</span>
                            <ArrowRight className="size-4" aria-hidden />
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="workspace-empty">
                  <Inbox aria-hidden />
                  <strong>
                    {failedKinds.length
                      ? "已读取的类别中暂无草稿"
                      : enabledKinds.length
                        ? "这里暂时没有草稿"
                        : "从你的职责开始"}
                  </strong>
                  <p>
                    {enabledKinds.length
                      ? "新起草的内容会在这里汇集，方便接着核对与准备。"
                      : "当前职责没有草稿类事项。你可以从常用工作进入，或查看自己的授权范围。"}
                  </p>
                  <Button asChild variant="outline">
                    <Link
                      href={
                        coordinator ? "/activities?mode=create" : author ? "/notices" : "/authority"
                      }
                    >
                      {coordinator ? "准备一场活动" : author ? "进入通知起草" : "查看我的授权"}
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              )}
              {!loading && enabledKinds.length > 0 ? (
                <p className="home-source-note">
                  <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>
                    各类最多读取 100 条，当前显示 {visibleRows.length}{" "}
                    条。完整记录请进入对应工作台。{readAt ? ` 读取于 ${readAt}。` : ""}
                  </span>
                </p>
              ) : null}
            </section>
            <div className="home-working-note">
              <Smartphone className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
              <div>
                <strong>桌面准备，手机接续</strong>
                <p>在这里起草、排版与配置；送审后，由相应负责人在手机端审查、放行。</p>
              </div>
            </div>
          </div>
          <aside className="home-aside" aria-label="近期安排与常用工作">
            <WorkbenchDataStatus state={workbench} />
            {coordinator ? (
              <section>
                <div className="workspace-section-head">
                  <h2>近期活动</h2>
                  <Link href="/activities">
                    查看列表
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
                {upcoming.loading ? (
                  <div className="workspace-loading" role="status" aria-label="正在读取近期活动">
                    <Skeleton />
                  </div>
                ) : upcoming.failed ? (
                  <div className="text-sm text-muted-foreground">
                    <p>暂时读不到近期活动。</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRefresh((value) => value + 1)}
                    >
                      重试
                    </Button>
                  </div>
                ) : upcoming.items.length === 0 ? (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    本次读取的已发布活动中，暂无尚未结束的安排。
                  </p>
                ) : (
                  <ul className="home-upcoming">
                    {upcoming.items.map((item) => {
                      const date = new Date(item.startAt);
                      return (
                        <li key={item.id}>
                          <Link href={`/activities?activity=${encodeURIComponent(item.id)}`}>
                            <span className="home-date" aria-hidden>
                              <span>{date.getMonth() + 1} 月</span>
                              <strong>{date.getDate()}</strong>
                            </span>
                            <span>
                              <strong className="home-upcoming__title">{item.title}</strong>
                              <small>
                                <time dateTime={item.startAt}>{formatTime(item.startAt)}</time>
                                <br />
                                {item.location}
                              </small>
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            ) : null}
            <section>
              <div className="workspace-section-head">
                <h2>常用工作</h2>
              </div>
              <div className="home-shortcuts">
                {shortcuts.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.href} href={item.href}>
                      <Icon className="size-4" aria-hidden />
                      {item.label}
                      <ArrowRight className="size-3" aria-hidden />
                    </Link>
                  );
                })}
                <Link href="/authority">
                  <ShieldCheck className="size-4" aria-hidden />
                  我的授权
                  <ArrowRight className="size-3" aria-hidden />
                </Link>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </Desk>
  );
}
